import { useState, useRef, useCallback } from 'react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
  DialogCloseTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Box, Text, VStack, HStack, Input } from '@chakra-ui/react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { apiUploadAgencyLogo } from '@/api/services/agency.service';
import { toaster } from '@/components/ui/toaster';
import { fetchAPI } from '@/api/fetch';
import { MAX_IMAGE_FILE_SIZE, MAX_IMAGE_FILE_SIZE_MB } from '@/config/constants';
import { Agency } from '@/types/Agency';

interface LogoUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (agency: Agency) => void;
}

const MAX_FILE_SIZE = MAX_IMAGE_FILE_SIZE;

export function LogoUploadDialog({ open, onClose, onSuccess }: LogoUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        toaster.create({
          title: 'File too large',
          description: `Please select an image smaller than ${MAX_IMAGE_FILE_SIZE_MB}MB`,
          type: 'error'
        });
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toaster.create({
          title: 'Invalid file type',
          description: 'Please select an image file',
          type: 'error'
        });
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result?.toString() || '');
      });
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImg = useCallback(
    async (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          0.95
        );
      });
    },
    []
  );

  const handleUpload = async () => {
    if (!completedCrop || !imgRef.current || !selectedFile) {
      toaster.create({
        title: 'No image selected',
        description: 'Please select and crop an image first',
        type: 'error'
      });
      return;
    }

    try {
      setUploading(true);
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
      const croppedFile = new File([croppedBlob], selectedFile.name, {
        type: 'image/jpeg'
      });

      const response = await fetchAPI<Agency>({
        request: () => apiUploadAgencyLogo({ payload: { file: croppedFile } })
      });

      if (response.success) {
        onSuccess(response.data);
        handleClose();
      } else {
        console.error('Upload failed:', response.message);
        toaster.create({
          title: 'Upload failed',
          description: 'Failed to upload logo. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toaster.create({
        title: 'Upload failed',
        description: 'Failed to upload logo. Please try again.',
        type: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImageSrc('');
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5
    });
    setCompletedCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => !e.open && handleClose()}
      size="lg"
      closeOnInteractOutside={false}
    >
      <DialogContent border="1px solid" borderColor="gray.200" borderRadius="16px">
        <DialogHeader>
          <DialogTitle>Upload Company Logo</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody>
          <VStack gap={4} align="stretch">
            <Box>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onSelectFile}
                style={{ display: 'none' }}
                id="logo-upload-input"
              />
              <Button
                variant="outline"
                width="full"
                borderRadius="8px"
                borderColor="neutrals.300"
                p="12px"
                fontSize="16px"
                color="neutrals.700"
                onClick={() => fileInputRef.current?.click()}
              >
                Select Image (Max {MAX_IMAGE_FILE_SIZE_MB}MB)
              </Button>
            </Box>

            {imageSrc && (
              <Box>
                <Text fontSize="16px" fontWeight="medium" mb="8px">
                  Crop your logo:
                </Text>
                <Box
                  border="1px solid"
                  borderColor="neutrals.300"
                  borderRadius="8px"
                  overflow="hidden"
                  maxH="400px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop
                  >
                    <img
                      ref={imgRef}
                      src={imageSrc}
                      alt="Crop preview"
                      style={{ maxHeight: '400px', maxWidth: '100%' }}
                    />
                  </ReactCrop>
                </Box>
              </Box>
            )}

            {!imageSrc && (
              <Box
                p={8}
                border="2px dashed"
                borderColor="neutrals.300"
                borderRadius="8px"
                textAlign="center"
              >
                <Text color="gray.500" fontSize="16px">
                  Select an image to upload and crop
                </Text>
              </Box>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <HStack gap={2}>
            <DialogActionTrigger asChild>
              <Button
                p="12px 24px"
                variant="outline"
                fontSize="16px"
                border="1px solid"
                borderRadius="8px"
                borderColor="primary.900"
                color="primary.900"
                disabled={uploading}
                onClick={handleClose}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              p="12px 24px"
              bg="primary.900"
              color="neutrals.0"
              fontSize="16px"
              borderRadius="8px"
              onClick={handleUpload}
              disabled={!completedCrop || uploading}
              loading={uploading}
            >
              Upload Logo
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
